import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import * as _ from 'lodash';
import { __, mapStateToProps } from '../core/utils';
import { setNotification } from '../redux/actions/general/notification';
import {
  listPlanningTemplates,
  readPlanningTemplate,
} from '../redux/actions/table/planning_library';
import {
  listProjectSubstages,
  loadProjectPlanningTemplate,
} from '../redux/actions/table/stages';
import { hideDropdown } from '../redux/actions/general/dropdown';
import Button from '../components/Button';
import Dropdown from '../components/Dropdown';
import Tooltip from '../components/Tooltip';
import SearchIcon from '../assets/images/search-15x15.svg';
import AddTemplateIcon from '../assets/images/add-boq-template-group-60x60.svg';
import LogoIcon from '../assets/images/logo-50x50.svg';
import ArrowDownIcon from '../assets/images/arrow-down-middle-15x15.svg';
import CheckedIcon from '../assets/images/checked-15x15.svg';
import NeutralIcon from '../assets/images/neutral-15x15.svg';
import UncheckedIcon from '../assets/images/unchecked-15x15.svg';
import styles from '../styles/wizards/ProjectPlanningTemplateWizard.scss';

const TYPE_LABELS = {
  stage: 'Stage',
  substage: 'Substage',
  group: 'Group',
};

const ProjectPlanningLoadFromTemplateWizard = ({
  templates: planningTemplates,
  stages,
  languageId,
  wizard,
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedLeafIds, setSelectedLeafIds] = useState([]);
  const [targetStageId, setTargetStageId] = useState('');
  const [step, setStep] = useState('select');
  const [submitting, setSubmitting] = useState(false);
  const mounted = useRef(true);
  const selectedIdRef = useRef(null);

  const projectId = _.get(wizard, 'project_id');
  const targetStages = useMemo(
    () => _.filter(stages, (stage) => stage.level === 0),
    [stages]
  );

  const getStageName = (stage) => {
    if (!stage) return '';

    const name = _.isString(stage.name)
      ? stage.name
      : _.get(stage.name, languageId) || _.find(stage.name);

    return stage.code ? `${stage.code} · ${name}` : name;
  };

  const targetStage = useMemo(
    () =>
      _.find(targetStages, (stage) =>
        _.isEqual(
          _.toString(stage.ownId || stage.id),
          _.toString(targetStageId)
        )
      ),
    [targetStages, targetStageId]
  );

  const selectTemplate = (id) => {
    if (step === 'preview') return;

    selectedIdRef.current = id;
    setSelectedId(id);
    setPreview(null);
    setSelectedLeafIds([]);

    readPlanningTemplate(id).then(({ response }) => {
      if (mounted.current && selectedIdRef.current === id) {
        setPreview(response.data);
      }
    });
  };

  useEffect(() => {
    mounted.current = true;

    listPlanningTemplates().then(({ response }) => {
      const first = _.first(response.data);

      if (mounted.current && first) {
        selectTemplate(first.id);
      }
    });

    return () => {
      mounted.current = false;
    };
  }, []);

  const selectableLeafIds = (nodes) => {
    return _.flatMap(nodes || [], (node) => {
      if (node.type === 'group') return [node.id];

      if (node.type === 'substage' && _.isEmpty(node.children)) {
        return [node.id];
      }

      return selectableLeafIds(node.children);
    });
  };

  const nodeSelection = (node) => {
    const leafIds = selectableLeafIds([node]);
    const selectedCount = _.size(_.intersection(leafIds, selectedLeafIds));

    if (!selectedCount) return 'unchecked';
    if (selectedCount === _.size(leafIds)) return 'checked';

    return 'neutral';
  };

  const toggleNode = (node) => {
    const leafIds = selectableLeafIds([node]);

    if (_.isEmpty(leafIds)) return;

    setSelectedLeafIds((currentSelectedLeafIds) => {
      const selectedCount = _.size(
        _.intersection(leafIds, currentSelectedLeafIds)
      );

      return selectedCount === _.size(leafIds)
        ? _.difference(currentSelectedLeafIds, leafIds)
        : _.union(currentSelectedLeafIds, leafIds);
    });
  };

  const toggleAll = () => {
    const leafIds = selectableLeafIds(preview?.nodes);

    setSelectedLeafIds((currentSelectedLeafIds) =>
      _.size(_.intersection(leafIds, currentSelectedLeafIds)) ===
      _.size(leafIds)
        ? []
        : leafIds
    );
  };

  const selectedTree = useMemo(
    () =>
      _.compact(
        _.map(preview?.nodes || [], (stage) => {
          const substages = _.compact(
            _.map(stage.children || [], (substage) => {
              const groups = _.filter(substage.children || [], (group) =>
                _.includes(selectedLeafIds, group.id)
              );
              const selected = _.isEmpty(substage.children)
                ? _.includes(selectedLeafIds, substage.id)
                : !_.isEmpty(groups);

              return selected ? { ...substage, children: groups } : null;
            })
          );

          return _.isEmpty(substages)
            ? null
            : { ...stage, children: substages };
        })
      ),
    [preview, selectedLeafIds]
  );

  const counts = useMemo(
    () => ({
      selected: _.size(selectedLeafIds),
      substages: _.sumBy(selectedTree, (stage) => _.size(stage.children)),
      groups: _.sumBy(selectedTree, (stage) =>
        _.sumBy(stage.children, (substage) => _.size(substage.children))
      ),
    }),
    [selectedLeafIds, selectedTree]
  );

  const review = () => {
    if (!preview || !targetStageId || _.isEmpty(selectedLeafIds)) {
      return;
    }

    setStep('preview');
  };

  const loadSelected = () => {
    if (submitting) return;

    setSubmitting(true);

    loadProjectPlanningTemplate(projectId, selectedId, {
      target_stage_id: Number(targetStageId),
      selected_node_ids: selectedLeafIds,
    })
      .then(() => {
        listProjectSubstages(projectId);
        setNotification(
          __('project_planning.load_template.notification.success')
        );
        onClose();
      })
      .catch(() => mounted.current && setSubmitting(false));
  };

  const renderCheckbox = (state) => {
    if (state === 'checked') {
      return <CheckedIcon className={styles.checked} />;
    }

    if (state === 'neutral') {
      return <NeutralIcon className={styles.neutral} />;
    }

    return <UncheckedIcon className={styles.unchecked} />;
  };

  const renderSelectionNodes = (nodes, depth = 0) => {
    return _.map(nodes || [], (node) => {
      const selection = nodeSelection(node);
      const leafIds = selectableLeafIds([node]);

      return (
        <React.Fragment key={node.id}>
          <button
            type='button'
            className={classNames(
              styles.selectionNode,
              node.type === 'stage' && styles.stageNode
            )}
            style={{ paddingLeft: `${16 + depth * 28}px` }}
            onClick={() => toggleNode(node)}
            disabled={_.isEmpty(leafIds)}
            aria-checked={
              selection === 'neutral' ? 'mixed' : selection === 'checked'
            }
            role='checkbox'
          >
            {renderCheckbox(selection)}
            <span className={classNames(styles.typeLabel, styles[node.type])}>
              {__(
                `planning-templates.type.${node.type}`,
                {},
                TYPE_LABELS[node.type]
              )}
            </span>
            <strong>{node.name}</strong>
          </button>
          {renderSelectionNodes(node.children, depth + 1)}
        </React.Fragment>
      );
    });
  };

  const renderPreviewRows = () => {
    const targetStageName = getStageName(targetStage);

    return _.map(selectedTree, (stage) => (
      <React.Fragment key={stage.id}>
        <div className={classNames(styles.previewRow, styles.previewStage)}>
          <div className={styles.previewSource}>
            <span className={classNames(styles.typeLabel, styles.stage)}>
              {__('planning-templates.type.stage')}
            </span>
            <strong>{stage.name}</strong>
          </div>
          <span className={styles.structureLabel}>
            {__('project_planning.load_template.preview.structure')}
          </span>
          <span />
        </div>

        {_.map(stage.children, (substage) => (
          <React.Fragment key={substage.id}>
            <div className={styles.previewRow}>
              <div
                className={styles.previewSource}
                style={{ paddingLeft: '30px' }}
              >
                <span className={classNames(styles.typeLabel, styles.substage)}>
                  {__('planning-templates.type.substage')}
                </span>
                <strong>{substage.name}</strong>
              </div>
              <span className={styles.addLabel}>
                {__('project_planning.load_template.preview.add')}
              </span>
              <span className={styles.previewTarget}>{targetStageName}</span>
            </div>

            {_.map(substage.children, (group) => (
              <div className={styles.previewRow} key={group.id}>
                <div
                  className={styles.previewSource}
                  style={{ paddingLeft: '58px' }}
                >
                  <span className={classNames(styles.typeLabel, styles.group)}>
                    {__('planning-templates.type.group')}
                  </span>
                  <strong>{group.name}</strong>
                </div>
                <span className={styles.addLabel}>
                  {__('project_planning.load_template.preview.add')}
                </span>
                <span className={styles.previewTarget}>{substage.name}</span>
              </div>
            ))}
          </React.Fragment>
        ))}
      </React.Fragment>
    ));
  };

  const normalizedSearch = _.toLower(_.trim(search));
  const templates = _.filter(planningTemplates, (template) =>
    _.includes(_.toLower(template.name), normalizedSearch)
  );
  const leafIds = selectableLeafIds(preview?.nodes);
  const allSelected =
    !_.isEmpty(leafIds) &&
    _.size(_.intersection(leafIds, selectedLeafIds)) === _.size(leafIds);
  const canReview = preview && targetStageId && !_.isEmpty(selectedLeafIds);

  return (
    <div className={styles.loadWizard}>
      <header className={styles.loadHeader}>
        <div>
          <strong>{__('project_planning.load_template.title')}</strong>
          <span>{__('project_planning.load_template.subtitle')}</span>
        </div>
        <AddTemplateIcon />
      </header>

      <div className={styles.searchBar}>
        <SearchIcon />
        <input
          autoFocus
          type='text'
          value={search}
          placeholder={__('planning-templates.chooser.search')}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className={styles.loadBody}>
        <div className={styles.templateList}>
          {_.map(templates, (template) => (
            <button
              type='button'
              key={template.id}
              className={classNames(
                styles.templateListItem,
                template.id === selectedId && styles.selected
              )}
              onClick={() => selectTemplate(template.id)}
            >
              <strong>{template.name}</strong>
              <span>
                {template.stages_count} stages · {template.substages_count}{' '}
                substages · {template.groups_count} groups
              </span>
              <Tooltip
                text={
                  template.is_system
                    ? __('planning-templates.source.system')
                    : __('planning-templates.source.my-company')
                }
              >
                <em
                  className={classNames(
                    styles.sourceLabel,
                    template.is_system ? styles.system : styles.company
                  )}
                >
                  {template.is_system ? <LogoIcon /> : 'MY'}
                </em>
              </Tooltip>
            </button>
          ))}
        </div>

        <div className={styles.loadContent}>
          <div className={styles.targetBar}>
            <label>
              {__('project_planning.load_template.field.target_stage')}
            </label>
            <Dropdown
              leftStyled
              left={0}
              top={42}
              name='project-planning-load-template-target-stage'
              readOnly={step === 'preview'}
              wrapperClassName={styles.targetDropdownWrapper}
              dropdownClassName={styles.targetDropdown}
              header={
                <div className={styles.targetDropdownHeader}>
                  {__('project_planning.load_template.field.target_stage')}
                </div>
              }
              content={
                <div className={styles.targetDropdownContent}>
                  {_.map(targetStages, (stage) => {
                    const stageId = stage.ownId || stage.id;
                    const selected = _.isEqual(
                      _.toString(stageId),
                      _.toString(targetStageId)
                    );

                    return (
                      <button
                        type='button'
                        key={stageId}
                        className={classNames(
                          styles.targetDropdownOption,
                          selected && styles.active
                        )}
                        onClick={() => {
                          setTargetStageId(stageId);
                          hideDropdown();
                        }}
                      >
                        <span>{getStageName(stage)}</span>
                        {selected && <CheckedIcon className={styles.checked} />}
                      </button>
                    );
                  })}
                </div>
              }
            >
              <div
                className={classNames(
                  styles.targetSelect,
                  step === 'preview' && styles.disabled
                )}
                role='button'
                aria-haspopup='listbox'
              >
                <span
                  className={classNames(!targetStage && styles.placeholder)}
                >
                  {targetStage
                    ? getStageName(targetStage)
                    : __('project_planning.load_template.select_stage')}
                </span>
                {step !== 'preview' && <ArrowDownIcon />}
              </div>
            </Dropdown>
          </div>

          {step === 'select' ? (
            <div className={styles.selectionContent}>
              {preview && (
                <div className={styles.templateSummary}>
                  <div>
                    <strong>{preview.name}</strong>
                    <span>
                      {__('project_planning.load_template.selection_hint')}
                    </span>
                  </div>
                  <Button
                    lightGray
                    medium
                    middleText={
                      allSelected
                        ? __('project_planning.load_template.clear_all')
                        : __('button.select-all')
                    }
                    onClick={toggleAll}
                    disabled={_.isEmpty(leafIds)}
                  />
                </div>
              )}

              <div className={styles.selectionTree}>
                {!preview ? (
                  <div className={styles.emptyState}>
                    {__('project_planning.load_template.loading')}
                  </div>
                ) : _.isEmpty(preview.nodes) ? (
                  <div className={styles.emptyState}>
                    {__('project_planning.load_template.empty')}
                  </div>
                ) : (
                  renderSelectionNodes(preview.nodes)
                )}
              </div>
            </div>
          ) : (
            <div className={styles.reviewContent}>
              <div className={styles.summaryCards}>
                <div>
                  <span>
                    {__('project_planning.load_template.preview.selected')}
                  </span>
                  <strong>{counts.selected}</strong>
                </div>
                <div>
                  <span>{__('planning-templates.type.substage')}</span>
                  <strong>{counts.substages}</strong>
                </div>
                <div>
                  <span>{__('planning-templates.type.group')}</span>
                  <strong>{counts.groups}</strong>
                </div>
              </div>

              <div className={styles.previewTable}>
                <div className={styles.previewTableHeader}>
                  <span>
                    {__('project_planning.load_template.preview.source')}
                  </span>
                  <span>
                    {__('project_planning.load_template.preview.action')}
                  </span>
                  <span>
                    {__('project_planning.load_template.preview.target')}
                  </span>
                </div>
                {renderPreviewRows()}
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className={styles.loadFooter}>
        <p>
          {step === 'select'
            ? __('project_planning.load_template.footer.selection')
            : __('project_planning.load_template.footer.preview')}
        </p>
        <div>
          {step === 'preview' && (
            <Button
              lightGray
              medium
              middleText={__('button.back')}
              onClick={() => setStep('select')}
              disabled={submitting}
            />
          )}
          <Button
            lightGray
            medium
            middleText={__('button.cancel')}
            onClick={onClose}
            disabled={submitting}
          />
          {step === 'select' ? (
            <Button
              medium
              middleText={__('project_planning.load_template.review')}
              onClick={review}
              disabled={!canReview}
            />
          ) : (
            <Button
              medium
              middleText={__('project_planning.load_template.confirm')}
              onClick={loadSelected}
              spinning={submitting}
              disabled={submitting}
            />
          )}
        </div>
      </footer>
    </div>
  );
};

export default mapStateToProps((state) => ({
  templates: state.table.planning_library.templates || [],
  stages:
    state.table.stages.initialSubstages || state.table.stages.substages || [],
  languageId: state.table.stages.main_language || state.auth.language_id,
  wizard: state.table.stages.load_project_planning_from_template,
}))(ProjectPlanningLoadFromTemplateWizard);
